import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { OfflineIndicator } from './OfflineIndicator';

/**
 * Layout for authenticated pages. The shell fills the viewport exactly
 * (h-screen + overflow-hidden) so the TopBar and Sidebar are truly
 * pinned and page content scrolls inside its own bounded area —
 * not past the chrome. Public routes (login, emergency-login,
 * magic-link forms) deliberately don't use this; they scroll the
 * whole page like a regular site.
 *
 * Density: admin routes get `data-density="compact"` which tightens
 * the Aurora spacing tokens (--space-card, --row-min) without
 * changing the colours. Power-user pages need info density; the
 * on-the-day surfaces stay roomy.
 */
export function AppShell() {
  const { pathname } = useLocation();
  const density = pathname.startsWith('/admin/') ? 'compact' : 'default';
  return (
    <div
      data-density={density}
      className="flex h-screen overflow-hidden bg-bg text-fg"
    >
      <Sidebar />
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <TopBar />
        <OfflineIndicator />
        {/*
         * The single scroll container for the right-hand content
         * column. Pages still use <main> internally; this wrapper
         * just bounds their scroll.
         *
         * min-h-0 + the !min-h-0 override on <main> together prevent
         * a phantom scrollbar on short pages (see the lengthy commit
         * history if this ever drifts back to broken).
         */}
        <div className="min-h-0 flex-1 overflow-y-auto [&>main]:!min-h-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
